import type { ComponentPropsWithoutRef, ElementType, ReactNode } from "react";
import type { ButtonProps } from "react-aria-components";
import { Button as ReactAriaButton } from "./aria-button";

import { IcExternal } from "@jetblue/kit-icons/ic-external";
import { IcLoading } from "@jetblue/kit-icons/ic-loading";
import { BUTTON_STYLES } from "./button-styles";
import type { ButtonSize, ButtonVariant } from "./button-types";
import { cn } from "../../utilities/tailwind";

export type ButtonProperties<C extends ElementType = "button"> = {
  /**
   * Defaults to `button`. This property allows the base element used to create the component to be changed.
   */
  as?: C | undefined;

  /**
   * Icon or content to display inside the button. Note: If a string is not the child, an `aria-label` will likely be required.
   */
  children: ReactNode;

  /**
   * Class name to apply to the outer container of the button
   */
  className?: string | undefined;

  /**
   * This property displays the `IcExternal` icon when set to true.
   */
  isExternal?: boolean | undefined;

  /**
   * This property expands the component to take up the full width of its parent component.
   */
  isFullWidth?: boolean | undefined;

  /**
   * While true, this property makes the button `children` screen reader only, updates the `aria-label` to `Loading...` and displays the `IcLoading` icon.
   */
  isLoading?: boolean | undefined;

  /**
   * Predetermined styling to represent different "sizes" of Buttons
   */
  size?: ButtonSize | undefined;

  /**
   * These are some basic variations on the styling of a Button
   */
  variant?: ButtonVariant;
} & Omit<
  ButtonProps,
  "children" | "className" | "isPending" | "style" | "slot"
> &
  ComponentPropsWithoutRef<C>;
export const Button = <C extends ElementType = "button">({
  as,
  className = "",
  children,
  isExternal = false,
  isFullWidth = false,
  isLoading = false,
  size = "medium",
  variant = "primary",
  ...properties
}: ButtonProperties<C>) => {
  const Component = as == "button" || !as ? ReactAriaButton : as;
  const isAnchorTag = Boolean(properties.href);

  const twClassName = cn(
    BUTTON_STYLES.base,
    BUTTON_STYLES.size[size],
    { [BUTTON_STYLES.fullWidth]: isFullWidth },
    isAnchorTag
      ? BUTTON_STYLES.linkColor[variant]
      : BUTTON_STYLES.color[variant],
    BUTTON_STYLES.disabled[variant],
    { [BUTTON_STYLES.loading]: isLoading },
    className,
  );

  return (
    <Component
      className={twClassName}
      {...properties}
      aria-label={isLoading ? "Loading..." : properties["aria-label"]}
    >
      <div
        className={
          isLoading
            ? "invisible flex items-center justify-center"
            : "flex items-center justify-center gap-1"
        }
      >
        {children}

        {isExternal ? <IcExternal /> : null}
      </div>

      {isLoading ? (
        <div className="absolute inset-0 flex size-full items-center justify-center pl-0.5">
          <IcLoading aria-hidden role="img" />
        </div>
      ) : null}
    </Component>
  );
};
Button.displayName = "Button";
